'use client';

import clsx from 'clsx';
import { FC, useState } from 'react';
import { Container } from 'atoms/Container';
import { Icon, IconType } from 'atoms/Icon';
import { StoryBlokLink } from 'atoms/StoryBlokLink';
import { Body } from 'atoms/Typography/Typography';
import { useProductDetailInfo } from 'pages/ProductDetail/hooks/useProductDetailInfo';
import { useTranslations } from 'modules/i18n/useTranslations';
import { Tooltip } from '../Tooltip';
import { WarrantyInfo } from '../WarrantyInfo';
import styles from './ProductBullets.module.css';

interface IProductBullets {
  bullets?: string[];
  tooltips?: boolean;
  size?: 'regular' | 'large';
  classes?: {
    root?: string;
    list?: string;
    bullet?: string;
  };
}

enum BulletType {
  PRICE = 'price',
  WARRANTY = 'warranty',
}

const PriceHelper: FC = () => {
  const t = useTranslations();
  const { priceLink } = useProductDetailInfo();
  return (
    <li>
      <Body type="xl" tag="span" weight="medium">
        {t('product.pdp.helpers.price')}{' '}
      </Body>

      {priceLink && (
        <StoryBlokLink link={priceLink} target="_blank">
          <Icon type={IconType.info} className={styles.infoIcon} />
        </StoryBlokLink>
      )}
    </li>
  );
};

const WarrantyHelper: FC = () => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const handleTooltip = (state: boolean = true) => {
    setOpen(state);
  };

  return (
    <li>
      <Body type="xl" tag="span" weight="medium">
        {t('product.pdp.helpers.warranty')}{' '}
      </Body>

      <Tooltip
        onClose={() => {
          handleTooltip(false);
        }}
        open={open}
        element={<WarrantyInfo />}
        className={styles.tooltip}
      >
        <Icon
          type={IconType.info}
          className={styles.infoIcon}
          onClick={() => {
            handleTooltip(true);
          }}
        />
      </Tooltip>
    </li>
  );
};

const Bullet: FC<{
  bullet: string;
  size?: 'regular' | 'large';
  className?: string;
}> = ({ bullet, size = 'regular', className }) => {
  if (bullet === BulletType.PRICE) {
    return <PriceHelper />;
  }
  if (bullet === BulletType.WARRANTY) {
    return <WarrantyHelper />;
  }
  return (
    <li>
      <Body
        type={size === 'large' ? 'xl' : 'sm'}
        tag="span"
        weight="medium"
        className={className}
      >
        {bullet}
      </Body>
    </li>
  );
};

export const ProductBullets: FC<IProductBullets> = ({
  bullets,
  tooltips,
  size,
  classes,
}) => {
  if (!bullets || bullets.length === 0) {
    return null;
  }

  const maxBullets = 5;
  const priceIndex = 3;
  const warrantyIndex = 4;
  const slicedBullets = bullets.slice(0, maxBullets);

  if (tooltips) {
    slicedBullets.splice(priceIndex, 0, BulletType.PRICE);
    slicedBullets.splice(warrantyIndex - 2, 0, BulletType.WARRANTY);
  }

  return (
    <ul
      className={clsx(
        styles.bulletList,
        tooltips && styles.withTooltips,
        classes?.list,
      )}
    >
      {slicedBullets.map((bullet, index) => (
        <Bullet
          key={index}
          bullet={bullet}
          size={size}
          className={classes?.bullet}
        />
      ))}
    </ul>
  );
};

export const ProductBulletsBlock: FC<IProductBullets> = ({
  bullets,
  tooltips,
  size,
  classes,
}) => {
  return !bullets?.length ? null : (
    <Container className={clsx(styles.root, classes?.root)}>
      <ProductBullets
        bullets={bullets}
        tooltips={tooltips}
        size={size}
        classes={classes}
      />
    </Container>
  );
};
